import PreviewBanner from "@/components/common/PreviewBanner";
import PrioritiesModal from "@/components/priorities/PrioritiesModal";
import PrioritiesTrigger from "@/components/priorities/PrioritiesTrigger";
import PriorityToasts from "@/components/priorities/PriorityToasts";
import { VoteSync } from "@/components/priorities/VoteSync";
import { CanvasProvider } from "@/contexts/CanvasContextProvider";
import { useCanvas } from "@/hooks/useCanvas";
import { useVotingStatus } from "@/hooks/useVotingStatus";
import { BackLinkConfig, CanvasLayoutContext } from "@/layouts/CanvasLayoutContext";
import AccessDeniedPage from "@/pages/AccessDeniedPage";
import ErrorPage from "@/pages/ErrorPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { useLanguageStore } from "@/stores/languageStore";
import { usePriorityStore } from "@/stores/priorityStore";
import { useIsMobile } from "@/ui";
import { floatingActionPositionStyle } from "@canucci/base-mantine/theme";
import { Box, Flex, Loader } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";
import i18n from "../config/i18n";
import { ApiError as CanvasApiError } from "../services/api";
import StandardLayout from "./StandardLayout";

function CanvasLayoutImpl({ fullWidth }: { fullWidth?: boolean }) {
  const location = useLocation();
  const {
    canvasResult: { data: canvasData, isPending, isError, error },
    externalId,
  } = useCanvas();

  const isPreviewMode = externalId === "--preview--";
  const { votingEnabled } = useVotingStatus();
  const insightsAvailable = canvasData?.capabilities?.can_view_insights ?? false;
  const maxVotesPerVoter = canvasData?.voting?.max_votes_per_voter ?? 3;
  const effectiveMaxVotesPerVoter = votingEnabled ? maxVotesPerVoter : 0;

  // Debug logging for voting status
  useEffect(() => {
    if (canvasData) {
      console.log("🗳️ Canvas voting status:", {
        votingEnabled,
        maxVotesPerVoter,
        canvasId: canvasData.synthesis_id,
        language: canvasData.resolved_language,
        rawVotingAccessMode: canvasData.voting?.access_mode,
      });
    }
  }, [canvasData, votingEnabled, maxVotesPerVoter]);

  const { t } = useTranslation(["pages", "common"]);
  const currentGlobalLanguage = useLanguageStore((state) => state.currentLanguage);
  const setGlobalLanguage = useLanguageStore((state) => state.setLanguage);
  const isMobile = useIsMobile();
  // State for the parent path, to be set by child pages via context
  const [parentPath, setParentPath] = useState<string | undefined>();
  const [backLink, setBackLink] = useState<BackLinkConfig | undefined>();
  const [title, setTitle] = useState<string | undefined>();
  const [subtitle, setSubtitle] = useState<string | undefined>();
  const [background, setBackground] = useState<string | undefined>();
  const [darkBackground, setDarkBackground] = useState<boolean | undefined>();
  const shellDarkBackground = location.pathname.endsWith("/action-plan") ? false : (darkBackground ?? true);

  const [langForApiQuery, setLangForApiQuery] = useState<string | undefined>(undefined);

  const apiError = error instanceof CanvasApiError ? error : null;

  // Priorities modal state - using granular selectors for performance
  const isModalOpen = usePriorityStore((state) => state.isModalOpen);
  const openModal = usePriorityStore((state) => state.openModal);
  const closeModal = usePriorityStore((state) => state.closeModal);
  const setCanvasId = usePriorityStore((state) => state.setCanvasId);
  const setMaxSelections = usePriorityStore((state) => state.setMaxSelections);

  // Update canvas ID in priority store when canvas changes
  useEffect(() => {
    if (externalId && !isPreviewMode) {
      setCanvasId(externalId);
    }
  }, [externalId, isPreviewMode, setCanvasId]);

  useEffect(() => {
    setMaxSelections(effectiveMaxVotesPerVoter);
  }, [effectiveMaxVotesPerVoter, setMaxSelections]);

  useEffect(() => {
    if (!votingEnabled && isModalOpen) {
      closeModal();
    }
  }, [votingEnabled, isModalOpen, closeModal]);

  useEffect(() => {
    if (isPreviewMode && canvasData?.resolved_language && currentGlobalLanguage !== canvasData.resolved_language) {
      setGlobalLanguage(canvasData.resolved_language);
    }
  }, [isPreviewMode, canvasData?.resolved_language, currentGlobalLanguage, setGlobalLanguage]);

  useEffect(() => {
    if (isPreviewMode) {
      return;
    }

    if (canvasData?.languages && canvasData.languages.length > 0) {
      let targetCanvasLanguage: string | undefined = undefined;
      const availableLanguages = canvasData.languages;
      const projectDefaultCanvasLang = canvasData.resolved_language || canvasData.canonical_language;
      const browserLanguages = i18n.languages || [];

      for (const browserLang of browserLanguages) {
        const simpleBrowserLang = browserLang.split("-")[0];
        if (availableLanguages.includes(browserLang)) {
          targetCanvasLanguage = browserLang;
          break;
        }
        if (availableLanguages.includes(simpleBrowserLang)) {
          targetCanvasLanguage = simpleBrowserLang;
          break;
        }
      }

      if (!targetCanvasLanguage && projectDefaultCanvasLang && availableLanguages.includes(projectDefaultCanvasLang)) {
        targetCanvasLanguage = projectDefaultCanvasLang;
      }

      if (!targetCanvasLanguage && availableLanguages.includes("en")) {
        targetCanvasLanguage = "en";
      }

      if (!targetCanvasLanguage && availableLanguages.length > 0) {
        targetCanvasLanguage = availableLanguages[0];
      }

      if (targetCanvasLanguage) {
        if (currentGlobalLanguage !== targetCanvasLanguage) {
          console.log(`Global language changing from ${currentGlobalLanguage} to ${targetCanvasLanguage} for canvas.`);
          setGlobalLanguage(targetCanvasLanguage);
        }
        if (langForApiQuery !== targetCanvasLanguage) {
          console.log(
            `Setting API query language for canvas to: ${targetCanvasLanguage}. (Previous: ${langForApiQuery})`,
          );
          setLangForApiQuery(targetCanvasLanguage);
        }
      }
    }
  }, [isPreviewMode, canvasData, currentGlobalLanguage, setGlobalLanguage, langForApiQuery, externalId]);

  return (
    <CanvasLayoutContext.Provider
      value={{
        setCanvasParentPath: setParentPath,
        setBackLink: setBackLink,
        setTitle: setTitle,
        setSubtitle: setSubtitle,
        setBackground: setBackground,
        setDarkBackground: setDarkBackground,
      }}
    >
      {isPreviewMode && <PreviewBanner />}
      <StandardLayout
        background={background}
        darkBackground={shellDarkBackground}
        showMenuButton={true}
        fullWidth={fullWidth}
        title={title}
        hasBackButton={isMobile && !!parentPath}
        subtitle={subtitle}
        parentPath={parentPath}
        backLink={backLink}
        availableLanguages={isPreviewMode ? [] : (canvasData?.languages ?? [])}
      >
        {/* Pending includes the auth wait before fetching; 401 is redirected by CanvasProvider. */}
        {isPending || apiError?.status === 401 ? (
          <Flex justify="center" align="center" h="calc(100vh - 100px)">
            <Loader size="xl" />
          </Flex>
        ) : isError || !canvasData ? (
          apiError && apiError.status === 404 ? (
            <NotFoundPage
              dark
              title={t("errors.canvasNotFound.title", { ns: "pages" })}
              message={t("errors.canvasNotFound.message", { ns: "pages" })}
            />
          ) : apiError && apiError.status === 410 ? (
            <ErrorPage
              dark
              title={t("errors.participationExpired.title", {
                ns: "pages",
                defaultValue: "This voting link has expired",
              })}
              message={t("errors.participationExpired.message", {
                ns: "pages",
                defaultValue: "Ask the facilitator to reopen voting and share the new link.",
              })}
            />
          ) : apiError && apiError.status === 403 ? (
            <AccessDeniedPage dark />
          ) : (
            <ErrorPage
              dark
              title={t("errors.generic.title", { ns: "pages" })}
              message={t("errors.generic.message", { ns: "pages" })}
            />
          )
        ) : (
          <Outlet />
        )}
      </StandardLayout>

      {/* Voting controls and closed-voting insights navigation */}
      {(votingEnabled || insightsAvailable) && canvasData && (
        <>
          {votingEnabled && (
            <>
              <VoteSync />
              <PriorityToasts />
            </>
          )}

          {/* Consistent FAB for all screen sizes */}
          <Box style={floatingActionPositionStyle}>
            <PrioritiesTrigger onClick={() => openModal()} insightsAvailable={insightsAvailable} />
          </Box>

          {votingEnabled && <PrioritiesModal open={isModalOpen} onClose={closeModal} />}
        </>
      )}
    </CanvasLayoutContext.Provider>
  );
}

export function CanvasLayout({
  fullWidth,
  mode = "canvas",
}: {
  fullWidth?: boolean;
  mode?: "canvas" | "participation";
}) {
  return (
    <CanvasProvider mode={mode}>
      <CanvasLayoutImpl fullWidth={fullWidth} />
    </CanvasProvider>
  );
}

export default CanvasLayout;
